import { useEffect, useState, useRef } from "react";
import data from "@/data/services/web-design/plans.json";
import eCommerceData from "@/data/services/e-commerce/plans.json";
import serversideTrackingData from "@/data/services/serverside-tracking/plans.json";
import priceListWeb from "@/data/services/web-design/price-list.json";
import priceListCommerce from "@/data/services/e-commerce/price-list.json";
import priceListServerside from "@/data/services/serverside-tracking/price-list.json";
import { useLocation, useParams } from "react-router-dom";
import PageSEO from "@/components/common/PageSEO";
import { Container, Row, Col } from "react-bootstrap";
import Spacing from "@/components/common/Spacing";
import Loading from "@/components/common/Loading";
import NotFound from "./NotFound";
import Intro from "@/components/service/components/intro/Intro";
import VisualBenefits from "@/components/service/components/visual-benefits/VisualBenefits";
import TextBubbles from "@/components/service/web-design/text-bubbles/TextBubbles";
import WebDesignIcons from "@/components/service/web-design/icons/Icons";
import PriceList from "@/components/service/components/price-list/PriceList";
import DynamicComparisonTable from "@/components/common/DynamicComparisonTable";
import Inclusives from "@/components/service/components/inclusives/Inclusives";
import Exclusives from "@/components/service/components/exclusives/Exclusives";
import EcommerceBanner from "@/components/service/components/ecommerce-banner/EcommerceBanner";
import ecommerceBanners from "@/data/services/e-commerce/banners.json";
import facebookIcon from "@/assets/facebook.png";
import instagramIcon from "@/assets/instagram.png";
import whatsappIcon from "@/assets/whatsapp.png";

export interface PlanData {
  id: string;
  title: string;
  text: string;
  description: string;
  textOne?: string;
  textTwo?: string;
  benefitsTitleTwo: string;
  benefitsTwo: {
    title: string;
  }[];
  iconsTitle: string;
  championLogo?: string;
  icons: {
    icon: string;
    text?: string;
  }[];
  inclusiveTitle?: string;
  inclusives?: string[];
  exclusiveTitle?: string;
  exclusives?: string[];
}

const plans: PlanData[] = [...data, ...eCommerceData, ...serversideTrackingData] as PlanData[];

const Plan = () => {
  const location = useLocation();
  const [showComparison, setShowComparison] = useState(false);
  const comparisonRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  const { id } = useParams<{ id: string }>();
  
  const scrollToComparison = () => {
    setShowComparison(true);
    setTimeout(() => {
      if (comparisonRef.current) {
        comparisonRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const hideComparison = () => {
    setShowComparison(false);
  };
  
  console.log(id);
  
  // Special case for ServerSide Tracking - default to enterprise plan
  const actualId = !id && location.pathname.includes("serverside-tracking") 
    ? "serverside-tracking-enterprise" 
    : id;
  
  if (!actualId) {
    return <Loading />;
  }

  const plan = plans.find((p) => p.id === actualId);

  if (!plan) {
    return <NotFound />;
  }

  const priceList = location.pathname.includes("web-design/")
    ? priceListWeb
    : location.pathname.includes("e-commerce/")
    ? priceListCommerce
    : location.pathname.includes("serverside-tracking")
    ? priceListServerside
    : priceListWeb;

  return (
    <>
      <PageSEO section="services" keyName={plan.id} />
      <Intro data={plan} />
      <Container>
        {plan.textOne && plan.textTwo && (
          <>
            <TextBubbles info={plan} />
            <Spacing size={4} />
          </>
        )}

      </Container>
      
      {/* ECOMMERCE BANNER - ABOVE CE INCLUDE SECTION */}
      {location.pathname.includes("e-commerce/") && ecommerceBanners[plan.id as keyof typeof ecommerceBanners] && (
        <EcommerceBanner data={ecommerceBanners[plan.id as keyof typeof ecommerceBanners]} />
      )}
      
      <VisualBenefits icon="bullet" data={plan} />

      {/* SERVERSIDE TRACKING PRICING SECTION */}
      {location.pathname.includes("serverside-tracking") && (
        <Container>
          <Spacing size={6} />
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <div style={{
                background: 'linear-gradient(135deg, #0a1001, #252623)',
                padding: '4rem 2rem',
                borderRadius: '20px',
                border: '2px solid #6bfbce',
                marginBottom: '3rem'
              }}>
                <h2 style={{ 
                  fontSize: '3rem', 
                  fontWeight: 'bold', 
                  marginBottom: '1rem',
                  color: '#6bfbce'
                }}>
                  €990 setup + €149/lună
                </h2>
                <p style={{ 
                  fontSize: '1.5rem', 
                  marginBottom: '2rem',
                  color: '#fcfff9'
                }}>
                  Tracking enterprise complet - zero compromisuri
                </p>
                <div style={{ 
                  fontSize: '1.3rem', 
                  marginBottom: '3rem',
                  color: '#aaf2d0',
                  fontWeight: 'bold'
                }}>
                  🎯 FREE DEMO AVAILABLE
                </div>
                <button
                  className="button body pack-button"
                  onClick={() => window.location.assign('/contact')}
                  style={{
                    padding: '1rem 2rem',
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}
                >
                  <span>VREAU DEMO GRATUIT</span>
                </button>
              </div>
            </Col>
          </Row>
          <Spacing size={6} />
        </Container>
      )}

      <Container>
        <Spacing size={4} />
        <WebDesignIcons info={plan} />
        <Spacing size={4} />

        {/* INCLUSIVES/EXCLUSIVES SECTION */}
        {plan.inclusiveTitle && plan.inclusives && (
          <Inclusives data={{ inclusiveTitle: plan.inclusiveTitle, inclusives: plan.inclusives }} />
        )}
        
        {plan.exclusiveTitle && plan.exclusives && (
          <Exclusives data={{ exclusiveTitle: plan.exclusiveTitle, exclusives: plan.exclusives }} />
        )}
        
        {/* CCSHOP DEMO SECTION - ABOVE PRICE CARDS */}
        {location.pathname.includes("e-commerce/") && (
          <div 
            className="ccshop-demo-section"
            style={{ 
              textAlign: 'center', 
              margin: '40px 0',
              padding: '40px 20px',
              borderRadius: '25px',
              border: '1px solid rgba(128, 128, 128, 0.5)',
              backgroundColor: '#252623'
            }}
          >
            <h2 style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              marginBottom: '20px'
            }}>
              Vezi CCshop în Acțiune
            </h2>
            <p style={{ 
              fontSize: '1.2rem', 
              marginBottom: '30px'
            }}>
              Aceasta E platforma construită cu aceleași tehnologii. Nu promisiuni - realitate live!
            </p>
            <button
              className="button body pack-button"
              onClick={() => window.open('https://ccshop.pixado.ro', '_blank')}
              style={{
                position: 'relative',
                padding: '20px 40px',
                minWidth: '300px',
                fontSize: '1.3rem',
                fontWeight: 'bold',
                margin: '0 auto',
                display: 'block'
              }}
            >
              <span style={{ position: 'relative', zIndex: 10 }}>
                VEZI CCSHOP LIVE
              </span>
            </button>
          </div>
        )}
        
        {!location.pathname.includes("serverside-tracking") && (
          <PriceList data={priceList} />
        )}
      </Container>

     {/* SOCIAL MEDIA SECTION - OUTSIDE PROBLEMATIC CONTAINER */}
     <Container style={{ 
       marginTop: isMobile ? '-5rem' : '-4rem'
     }}>
       <Row className="contact-submit-row">
         <Col xs={12} lg={6}>
           {/* Empty left side */}
         </Col>
         <Col className="submit-second-col" xs={12} lg={{ span: 4, offset: 2 }}>
           <Row className="body">Hai cu noi pe social media!</Row>
           <Row>
             <Col xs={4}>
               <a href="https://www.facebook.com/pixadoagency" target="_blank" rel="noopener noreferrer">
                 <img src={facebookIcon} className="contact-dot" alt="Facebook" />
               </a>
             </Col>
             <Col xs={4}>
               <a href="https://www.instagram.com/pixadoagency/" target="_blank" rel="noopener noreferrer">
                 <img src={instagramIcon} className="contact-dot" alt="Instagram" />
               </a>
             </Col>
             <Col xs={4}>
               <a href="https://wa.me/40745371620" target="_blank" rel="noopener noreferrer">
                 <img src={whatsappIcon} className="contact-dot" alt="WhatsApp" />
               </a>
             </Col>
           </Row>
         </Col>
       </Row>
     </Container>
      
      <Container style={{ height: "100%", marginTop: '-2rem' }}>
        {/* SHOW BUTTON ONLY FOR E-COMMERCE PAGES WHEN TABLE IS HIDDEN */}
        {location.pathname.includes("e-commerce/") && !showComparison && (
          <div 
            className="comparison-button-container"
            style={{ 
              textAlign: 'center', 
              margin: '40px 0',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <button
              className="button body"
              onClick={scrollToComparison}
              style={{
                position: 'relative',
                padding: '12px 24px',
                minWidth: '200px',
                margin: '0 auto',
                display: 'block'
              }}
            >
              <span style={{ position: 'relative', zIndex: 10 }}>
                Vezi tabel comparativ side-by-side
              </span>
            </button>
          </div>
        )}

        {/* SHOW TABLE WHEN STATE IS TRUE */}
        {showComparison && (
          <>
            <div ref={comparisonRef} id="comparison">
              <DynamicComparisonTable 
                fileUrl="/data/shop.tiers.xlsx"
                title="CC Shop - Comparatie Completa"
                className="shop-comparison"
              />
            </div>
            
            {/* HIDE BUTTON BELOW TABLE */}
            <div 
              className="comparison-button-container"
              style={{ 
                textAlign: 'center', 
                margin: '40px 0',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <button 
                className="button body" 
                onClick={hideComparison}
                style={{
                  position: 'relative',
                  padding: '12px 24px',
                  minWidth: '200px',
                  margin: '0 auto',
                  display: 'block'
                }}
              >
                <span style={{ 
                  position: 'relative', 
                  zIndex: 10 
                }}>
                  Inchide tabelul
                </span>
              </button>
            </div>
          </>
        )}
      </Container>

    </>
  );
};

export default Plan;

